import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { COMPANY, PROJECT, LIVECAMALL, TIMELAPSEALL } from "../../services/actions";
import { MdBatteryChargingFull, MdBolt, MdOutlineSolarPower, MdCamera } from "react-icons/md";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, InputLabel, FormControl } from "@mui/material";
import Swal from "sweetalert2";
import axios from "axios";
import "./main.css";

const statusStyles = {
    online: {
        borderColor: "#22c55e",
        glow: "0 0 12px rgba(34, 197, 94, 0.35)",
        badgeColor: "#22c55e",
    },
    warning: {
        borderColor: "#facc15",
        glow: "0 0 12px rgba(250, 204, 21, 0.35)",
        badgeColor: "#facc15",
    },
    offline: {
        borderColor: "#ef4444",
        glow: "0 0 12px rgba(239, 68, 68, 0.35)",
        badgeColor: "#ef4444",
    },
};
const gray = {
    "backgroundColor": "#f1f1f1",
    "color": "#94a3b8", padding: 8,
    borderRadius: 16
}
function DeviceRow({ title, icon, data, progress = null, exclude = [], style = {}, replaceKey = "" }) {
    const gray = {
        "backgroundColor": "#f1f1f1",
        "color": "#94a3b8", padding: 8,
        borderRadius: 16
    }
    return <div className="section device-row" style={gray}>
        <div style={{ display: 'flex', width: '100%', gap: 12 }}>
            <div className="device-icon-container" style={{ padding: 10, minWidth: 52, backgroundColor: '#ffffffff', borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {icon}
                <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: 10, textTransform: 'uppercase' }}>{title}</span>
            </div>
            <div style={{ width: '100%' }}>
                <div className="battery-row">
                    {progress && data.map((d) => {
                        if (d.name == progress) {
                            return <>
                                <span className="input-label text-title" style={{ fontWeight: 600, color: "#464747ff", fontSize: 14 }}>{d.value || 0}%</span>
                            </>
                        }
                    })}
                </div>
                <div className="grid">
                    {data.map((d) => {
                        if (exclude.includes(d.name)) return
                        const n = d.name.startsWith("Battery Temperature") ? "Temp" : d.name
                        return <div>
                            <strong className="input-label text-value" style={{ fontWeight: 600, color: "#4f5150ff", fontSize: 14 }}>{d.value || 0}</strong>
                            <span className="text-title" style={{ fontWeight: 400, color: "#464747ff", fontSize: 10 }}>{n.replaceAll(replaceKey, "")}</span>
                        </div>
                    })}
                </div>
            </div>
        </div>
    </div>
}

export default function DeviceCard({
    deviceId,
    title = "WL-01",
    location = "Tower A · North",
    initialMapping = null,
    status = "online",
    battery = 82,
    solarVoltage = "18.5 V",
    solarCurrent = "4.2 A",
    solarPower = "77 W",
    solarData = [],
    batteryData = [],
    loadData = [],
}) {
    const style = statusStyles[status] || statusStyles.offline;
    const navigate = useNavigate();
    const [openLocationForm, setOpenLocationForm] = useState(false);
    const [company, setCompany] = useState("");
    const [project, setProject] = useState("");
    const [camera, setCamera] = useState("");

    const [mappedCamera, setMappedCamera] = useState(() => {
        if (initialMapping) return initialMapping;
        try {
            const stored = JSON.parse(localStorage.getItem("device_camera_mapping") || "{}");
            return stored[String(deviceId || title)] || null;
        } catch (e) {
            return null;
        }
    });

    useEffect(() => {
        if (initialMapping) {
            setMappedCamera(initialMapping);
        } else {
            try {
                const stored = JSON.parse(localStorage.getItem("device_camera_mapping") || "{}");
                if (stored[String(deviceId || title)]) {
                    setMappedCamera(stored[String(deviceId || title)]);
                }
            } catch (e) {}
        }
    }, [initialMapping, deviceId, title]);

    const dispatch = useDispatch();
    const companyState = useSelector((state) => state?.companyState);
    const projectState = useSelector((state) => state?.projectState);
    const livecamallState = useSelector((state) => state?.livecamallState);
    const timelapseallState = useSelector((state) => state?.timelapseallState);

    const combinedCameras = React.useMemo(() => {
        const list = [];
        const seenNames = new Set();
        const addCamera = (cam) => {
            if (cam && cam.camera_name && !seenNames.has(cam.camera_name)) {
                seenNames.add(cam.camera_name);
                list.push(cam);
            }
        };
        (livecamallState?.data?.results || []).forEach(addCamera);
        (timelapseallState?.data?.results || []).forEach(addCamera);
        return list;
    }, [livecamallState?.data?.results, timelapseallState?.data?.results]);

    useEffect(() => {
        if (openLocationForm) {
            dispatch({ type: `${COMPANY}_GET`, payload: 0 });
            if (mappedCamera) {
                if (mappedCamera.company_id) {
                    setCompany(mappedCamera.company_id);
                    dispatch({
                        type: `${PROJECT}_GET`,
                        payload: `project/0/?filter_by={"company":"${mappedCamera.company_id}"}`,
                    });
                }
                if (mappedCamera.project_id) {
                    setProject(mappedCamera.project_id);
                    dispatch({
                        type: `${LIVECAMALL}_GET`,
                        payload: `livecam/0/?filter_by={"project_id":"${mappedCamera.project_id}"}`
                    });
                    dispatch({
                        type: `${TIMELAPSEALL}_GET`,
                        payload: `timelapse/0/?filter_by={"project_id":"${mappedCamera.project_id}"}`
                    });
                }
                if (mappedCamera.camera_id) {
                    setCamera(mappedCamera.camera_id);
                }
            }
        }
    }, [openLocationForm, dispatch, mappedCamera]);

    const handleCompanyChange = (e) => {
        const val = e.target.value;
        setCompany(val);
        setProject("");
        setCamera("");
        if (val) {
            dispatch({
                type: `${PROJECT}_GET`,
                payload: `project/0/?filter_by={"company":"${val}"}`,
            });
        }
    };

    const handleProjectChange = (e) => {
        const val = e.target.value;
        setProject(val);
        setCamera("");
        if (val) {
            dispatch({
                type: `${LIVECAMALL}_GET`,
                payload: `livecam/0/?filter_by={"project_id":"${val}"}`
            });
            dispatch({
                type: `${TIMELAPSEALL}_GET`,
                payload: `timelapse/0/?filter_by={"project_id":"${val}"}`
            });
        }
    };

    const handleSave = async () => {
        const selectedCompany = companyState?.data?.results?.find(c => String(c.id) === String(company));
        const selectedProject = projectState?.data?.results?.find(p => String(p.id) === String(project));
        const selectedCam = combinedCameras.find(c => String(c.id) === String(camera));

        if (!camera && !selectedCam) {
            Swal.fire({
                icon: "warning",
                title: "Select Camera",
                text: "Please select a camera to map.",
                timer: 2000
            });
            return;
        }

        const newMapping = {
            device_id: String(deviceId || title),
            device_name: title,
            company_id: company,
            company_name: selectedCompany?.company_name || "",
            project_id: project,
            project_name: selectedProject?.project_name || "",
            camera_id: camera,
            camera_name: selectedCam?.camera_name || "Camera",
            camera_type: selectedCam?.camera_type || "livecam"
        };

        // Local storage sync
        try {
            const stored = JSON.parse(localStorage.getItem("device_camera_mapping") || "{}");
            stored[String(deviceId || title)] = newMapping;
            localStorage.setItem("device_camera_mapping", JSON.stringify(stored));
        } catch (e) {}

        // Backend API sync
        try {
            await axios.post("solar/device-camera-mapping/", newMapping);
        } catch (e) {
            console.log("Backend mapping sync error / fallback to local:", e);
        }

        setMappedCamera(newMapping);
        setOpenLocationForm(false);
        Swal.fire({
            icon: "success",
            title: "Camera Mapped!",
            text: `Successfully mapped "${newMapping.camera_name}" to ${title}.`,
            timer: 2000,
            showConfirmButton: false
        });
    };

    const handleUnmap = async () => {
        try {
            const stored = JSON.parse(localStorage.getItem("device_camera_mapping") || "{}");
            delete stored[String(deviceId || title)];
            localStorage.setItem("device_camera_mapping", JSON.stringify(stored));
        } catch (e) {}

        try {
            await axios.delete(`solar/device-camera-mapping/?device_id=${deviceId || title}`);
        } catch (e) {}

        setMappedCamera(null);
        setCompany("");
        setProject("");
        setCamera("");
        setOpenLocationForm(false);
        Swal.fire({
            icon: "info",
            title: "Unmapped",
            text: "Camera mapping removed.",
            timer: 1500,
            showConfirmButton: false
        });
    };

    const isDark = localStorage?.selectedTheme === 'dark';
    const textColor = isDark ? 'white' : 'black';

    return (
        <div
            className="device-card shadow"
            style={{
                boxShadow: style.glow, height: '100%',
                backgroundColor: 'white',
                color: 'black'
            }}
        >
            <div className="device-header input-label">
                <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
                    {mappedCamera ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#1976d2',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4
                                }}
                                onClick={() => {
                                    navigate('/cameras/');
                                }}
                                title="Click to view camera"
                            >
                                <MdCamera style={{ fontSize: 14 }} />
                                {mappedCamera.camera_name || "Camera"}
                            </span>
                            <span
                                style={{ cursor: 'pointer', fontSize: 12, opacity: 0.7 }}
                                onClick={() => setOpenLocationForm(true)}
                                title="Change mapping"
                            >
                                ✏️
                            </span>
                        </div>
                    ) : (
                        <div style={{ marginTop: 4 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setOpenLocationForm(true)}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    py: 0.1,
                                    px: 1,
                                    borderRadius: '6px',
                                    borderColor: '#3b82f6',
                                    color: '#2563eb',
                                    lineHeight: 1.5,
                                    '&:hover': {
                                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                                        borderColor: '#2563eb'
                                    }
                                }}
                            >
                                + Map Camera
                            </Button>
                        </div>
                    )}
                </div>
                <span style={{ display: 'flex', gap: 2, alignItems: 'center', border: `1px solid ${style.badgeColor}`, padding: '2px 12px', borderRadius: 12 }}>
                    <div
                        className="status-badge"
                        style={{ backgroundColor: style.badgeColor, width: 5, height: 5, padding: 4 }}
                    >
                    </div>
                    <span
                        style={{ color: style.badgeColor }}
                    >
                        {status}
                    </span>
                </span>
            </div>

            <DeviceRow
                title={"SOLAR"}
                icon={<MdOutlineSolarPower style={{ fontSize: 24, color: '#0088ff' }} />}
                data={solarData}
                exclude={["Load Status"]}
                style={style}
                replaceKey="PV"
            />

            <DeviceRow
                title={"BATTERY"}
                icon={<MdBatteryChargingFull style={{ fontSize: 24, color: style?.badgeColor }} />}
                data={batteryData}
                exclude={["Charging Status", "Battery SOC", "Maximum Battery Voltage", "Minimum Battery Voltage"]}
                style={style}
                progress={"Battery SOC"}
                replaceKey="Battery"
            />


            <DeviceRow
                title={"LOAD"}
                icon={<MdBolt style={{ fontSize: 24, color: statusStyles.warning.badgeColor }} />}
                data={loadData}
                exclude={["Load Status"]}
                style={style}
                replaceKey="Load"
            />

            <div style={{ display: 'flex', gap: 2, fontSize: 13, justifyContent: 'space-between' }}>
                <div>
                    <span>Camera</span>
                    <span> ON</span>
                </div>
                <div>
                    <span>Load</span>
                    <span style={{ color: style?.badgeColor }}> ON</span>
                </div>
            </div>

            <Dialog
                open={openLocationForm}
                onClose={() => setOpenLocationForm(false)}
                fullWidth
                maxWidth="sm"
                BackdropProps={{
                    sx: {
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        backgroundColor: 'rgba(15, 23, 42, 0.7)'
                    }
                }}
                PaperProps={{
                    sx: {
                        backgroundColor: localStorage?.selectedTheme === 'dark' ? '#1e2229 !important' : '#ffffff !important',
                        borderRadius: '16px',
                        border: '1px solid #cbd5e1',
                        color: localStorage?.selectedTheme === 'dark' ? 'white' : 'black',
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ color: textColor, fontWeight: 700, fontSize: 17 }}>
                    {mappedCamera ? `Edit Camera Mapping for ${title}` : `Map Camera to ${title}`}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <InputLabel sx={{ color: textColor }}>Company</InputLabel>
                        <Select
                            value={company}
                            label="Company"
                            onChange={handleCompanyChange}
                            sx={{
                                borderRadius: '10px',
                                color: textColor,
                                '.MuiOutlinedInput-notchedOutline': { borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: textColor },
                                '.MuiSvgIcon-root': { color: textColor }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: localStorage?.selectedTheme === 'dark' ? '#1e2229' : '#ffffff',
                                        color: localStorage?.selectedTheme === 'dark' ? 'white' : 'black',
                                    }
                                }
                            }}
                        >
                            {companyState?.data?.results?.map((c) => (
                                <MenuItem key={c.id} value={c.id}>{c.company_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: textColor }}>Project</InputLabel>
                        <Select
                            value={project}
                            label="Project"
                            onChange={handleProjectChange}
                            sx={{
                                borderRadius: '10px',
                                color: textColor,
                                '.MuiOutlinedInput-notchedOutline': { borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: textColor },
                                '.MuiSvgIcon-root': { color: textColor }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: localStorage?.selectedTheme === 'dark' ? '#1e2229' : '#ffffff',
                                        color: localStorage?.selectedTheme === 'dark' ? 'white' : 'black',
                                    }
                                }
                            }}
                        >
                            {projectState?.data?.results?.map((p) => (
                                <MenuItem key={p.id} value={p.id}>{p.project_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: textColor }}>Camera</InputLabel>
                        <Select
                            value={camera}
                            label="Camera"
                            onChange={(e) => setCamera(e.target.value)}
                            sx={{
                                borderRadius: '10px',
                                color: textColor,
                                '.MuiOutlinedInput-notchedOutline': { borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: textColor },
                                '.MuiSvgIcon-root': { color: textColor }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: localStorage?.selectedTheme === 'dark' ? '#1e2229' : '#ffffff',
                                        color: localStorage?.selectedTheme === 'dark' ? 'white' : 'black',
                                    }
                                }
                            }}
                        >
                            {combinedCameras.map((cam) => (
                                <MenuItem key={cam.id || cam.camera_name} value={cam.id}>{cam.camera_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                        {mappedCamera && (
                            <Button
                                color="error"
                                sx={{ borderRadius: '8px', textTransform: 'none' }}
                                onClick={handleUnmap}
                            >
                                Unmap
                            </Button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            sx={{ borderRadius: '8px', textTransform: 'none', color: '#64748b' }}
                            onClick={() => setOpenLocationForm(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                backgroundColor: '#2563eb',
                                px: 2.5
                            }}
                            onClick={handleSave}
                        >
                            Save Mapping
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        </div>
    );
}